/**
 * Tutar — tasarım sisteminin İMZA bileşeni.
 *
 * Yön kodlamasını uygular: BİRİNCİL METİN para giren, ETİKET KIRMIZISI
 * para çıkan. Ancak renk TEK BAŞINA anlam taşımaz (WCAG 1.4.1): işaret (+/-)
 * her zaman yazılır ve ekran okuyucuya açık bir Türkçe etiket verilir.
 *
 * Para her zaman STRING olarak gelir (ADR-03). Bu bileşen hiçbir koşulda
 * Number()/parseFloat() çağırmaz; biçimlendirmeyi @kantin/shared'daki
 * paraGoster yapar.
 */
import type { CSSProperties, ReactNode } from "react";
import { para, paraGoster } from "@kantin/shared";

export type TutarBoyu = "sm" | "md" | "lg" | "xl" | "2xl";

export interface TutarOzellikleri {
  /** "50.00" biçminde string veya Decimal */
  deger: string;
  /**
   * Yön kodlamasını kapatır; nötr sunum (örn. "toplam ciro") için.
   * Varsayılan: açık — yön kodlaması uygulanır.
   */
  yonsuz?: boolean;
  /** İşareti her zaman göster (+12,50 gibi) */
  isaretiGoster?: boolean;
  /**
   * Offline'da bakiye KESİN DEĞİL, TAHMİNİDİR (§M4 kırmızı çizgisi).
   * true ise başına "~" konur ve ekran okuyucuya "tahmini" denir.
   * Bu ayrımı UI'da silikleştirmek, sahada güven kaybının bir numaralı sebebi.
   */
  tahmini?: boolean;
  boy?: TutarBoyu;
  simge?: string;
  className?: string;
  style?: CSSProperties;
  /** Tutarın ne olduğunu ekran okuyucuya söyler: "kalan bakiye" gibi */
  etiket?: string;
  cocuk?: ReactNode;
}

const BOY_SINIF: Record<TutarBoyu, string> = {
  sm: "text-sm",
  md: "text-md font-satir",
  lg: "text-lg font-bolum",
  xl: "text-xl font-bolum",
  "2xl": "text-2xl font-bolum",
};

export function Tutar({
  deger,
  yonsuz = false,
  isaretiGoster = false,
  tahmini = false,
  boy = "md",
  simge = "₺",
  className = "",
  style,
  etiket,
}: TutarOzellikleri) {
  const d = para(deger);
  const negatif = d.isNegative();
  const sifir = d.isZero();

  const renk =
    yonsuz || sifir ? "text-murekkep" : negatif ? "text-cikan" : "text-giren";

  const govde = paraGoster(d.abs(), simge);
  const isaret = negatif ? "−" : isaretiGoster && !sifir ? "+" : "";
  const onEk = tahmini ? "~" : "";

  const okunus = [
    etiket,
    tahmini ? "tahmini" : null,
    negatif ? "eksi" : isaretiGoster && !sifir ? "artı" : null,
    govde,
    yonsuz || sifir ? null : negatif ? "(çıkan)" : "(giren)",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={`tutar rakam whitespace-nowrap font-mono ${renk} ${BOY_SINIF[boy]} ${className}`}
      style={style}
    >
      <span aria-hidden="true">
        {onEk}
        {isaret}
        {govde}
      </span>
      <span className="gizli-erisilebilir">{okunus}</span>
    </span>
  );
}

/**
 * Bakiye tazeliği göstergesi — KK-313.
 * "bakiye: ~42,50 ₺ (14:20 itibarıyla)". Kasa offline'ken bunu gizlemek yasak.
 */
export function BakiyeTazeligi({ zaman }: { zaman: Date | string | null }) {
  if (!zaman) return null;
  const d = typeof zaman === "string" ? new Date(zaman) : zaman;
  const saat = d.toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <span className="text-xs text-murekkep-3">
      {saat} itibarıyla
      <span className="gizli-erisilebilir">
        {" "}
        — bu bakiye {saat} itibarıyladır, güncel olmayabilir
      </span>
    </span>
  );
}
